import React, { useState } from 'react';
import styled from 'astroturf/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faThumbsDown } from '@fortawesome/pro-solid-svg-icons';
import {
  faThumbsUp as faThumbsUpOutline,
  faThumbsDown as faThumbsDownOutline,
} from '@fortawesome/pro-regular-svg-icons';

import { Spinner } from 'js/academia_components/transients/spinner';
import { Button } from 'js/design_system/Button';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import { ExpandingTextArea } from './InputForm';
import { FeedbackRating } from './api';

interface Props extends React.ComponentPropsWithoutRef<'form'> {
  reportId?: string;
  searchQuery?: string;
  sessionId?: string;
}

export default function FeedbackForm({
  reportId,
  searchQuery,
  sessionId,
  ...formProps
}: Props) {
  const [rating, setRating] = useState<FeedbackRating | undefined>(undefined);
  const [freeform, setFreeform] = useState('');
  const [surveyId, setSurveyId] = useState<number | undefined>(undefined);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleRatingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rating = e.target.value as FeedbackRating;
    setRating(rating);
    const feedback = {
      reportId,
      searchQuery,
      sessionId,
      surveyId,
      rating,
      freeform,
    };
    submitFeedback(feedback).then((id) => setSurveyId(id));
  };

  const handleFreeformSubmit = () => {
    if (freeform.trim().length === 0) {
      return;
    }

    const feedback = {
      reportId,
      searchQuery,
      sessionId,
      surveyId,
      rating,
      freeform,
    };
    setIsSubmitting(true);
    submitFeedback(feedback).then((id) => {
      setSurveyId(id);
      if (freeform.trim().length > 0) {
        setHasSubmitted(true);
      }
      setIsSubmitting(false);
    });
  };

  return (
    <Form {...formProps}>
      <RowWrap>
        <FieldLabel>
          How satisfied are you with these search results?
        </FieldLabel>
        <Row>
          <input
            type="radio"
            id="rating_good"
            name="rating"
            value="good"
            onChange={handleRatingChange}
            style={{ display: 'none' }}
          />
          <ThumbIcon htmlFor="rating_good">
            {rating === 'good' ? (
              <FontAwesomeIcon
                style={{ width: '1.3em', height: '1.3em' }}
                icon={faThumbsUp}
              />
            ) : (
              <FontAwesomeIcon
                style={{ width: '1.3em', height: '1.3em' }}
                icon={faThumbsUpOutline}
              />
            )}
          </ThumbIcon>

          <input
            type="radio"
            id="rating_bad"
            name="rating"
            value="bad"
            onChange={handleRatingChange}
            style={{ display: 'none' }}
          />
          <ThumbIcon htmlFor="rating_bad">
            {rating === 'bad' ? (
              <FontAwesomeIcon
                style={{
                  width: '1.3em',
                  height: '1.3em',
                  transform: 'scale(-1,1)',
                }}
                icon={faThumbsDown}
              />
            ) : (
              <FontAwesomeIcon
                style={{
                  width: '1.3em',
                  height: '1.3em',
                  transform: 'scale(-1,1)',
                }}
                icon={faThumbsDownOutline}
              />
            )}
          </ThumbIcon>
        </Row>
      </RowWrap>

      <Textarea
        rows={2}
        placeholder="Tell us how we can do better"
        value={freeform}
        onChange={(e) => setFreeform(e.target.value)}
      />

      <RowWrap>
        <ImpressionWatcher source="refutation_finder.feedback_survey_submit">
          <SubmitButton
            size="sm"
            type="button"
            onClick={handleFreeformSubmit}
            disabled={isSubmitting}
            icon={isSubmitting ? <Spinner show /> : undefined}
          >
            {isSubmitting ? undefined : 'Submit Feedback'}
          </SubmitButton>
        </ImpressionWatcher>

        {hasSubmitted && (
          <SubmittedText>Thank you for your feedback</SubmittedText>
        )}
      </RowWrap>
    </Form>
  );
}

// Use the MultipleChoiceSurveyResponse model via the SurveyResponsesController,
// but with only one choice.
interface MultipleChoiceSurveyResponse {
  id?: number;
  survey_type: 'refutation_finder_feedback';
  survey_version: 3;
  choices?: [FeedbackRating];
  freeform?: string;
  extra_data: {
    reportId?: string;
    searchQuery?: string;
    sessionId?: string;
  };
}

async function submitFeedback({
  reportId,
  searchQuery,
  sessionId,
  surveyId,
  rating,
  freeform,
}: {
  reportId?: string;
  searchQuery?: string;
  sessionId?: string;
  surveyId?: number;
  rating?: FeedbackRating;
  freeform?: string;
}): Promise<number | undefined> {
  const body: MultipleChoiceSurveyResponse = {
    survey_type: 'refutation_finder_feedback',
    survey_version: 3,
    extra_data: {
      reportId,
      searchQuery,
      sessionId,
    },
  };

  if (surveyId) {
    body['id'] = surveyId;
  }
  if (rating) {
    body['choices'] = [rating];
  }
  if (freeform) {
    body['freeform'] = freeform;
  }

  let url = '';
  let method = '';
  if (!surveyId) {
    url = routes.api_v0_survey_submit_path({ subdomain_param: 'api' });
    method = 'POST';
  } else {
    url = routes.api_v0_survey_update_path({ subdomain_param: 'api' });
    method = 'PUT';
  }

  try {
    const response = await fetchWithCsrf(url, {
      method,
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.ok) {
      const json = await response.json();
      return json?.id ?? surveyId;
    }
  } catch {
    return;
  }
}

const Form = styled.form`
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const RowWrap = styled(Row)`
  flex-wrap: wrap;
`;

const FieldLabel = styled.label`
  font-weight: 500;
`;

const ThumbIcon = styled.label`
  svg {
    &:hover {
      cursor: pointer;
    }
  }
`;

const Textarea = styled(ExpandingTextArea)`
  line-height: 1.2;
  background: white;
  width: 100%;
  padding: 12px;
  border: 1px solid #b1b1ba;
  border-radius: 8px;
`;

const SubmitButton = styled(Button)`
  width: fit-content;
`;

const SubmittedText = styled.div`
  font-weight: 500;
`;
